<template>
  <div class="animated fadeIn">
    <div class="row">

      <div class="col-xl">
        <b-card>
          <template slot="header">
            <survey-filter v-model="surveyFilter" :disabled="isExporting" :multipleSurveySelection="false" @input="loadData" />
          </template>

          <b-form-group :label-cols-sm="2" :label="$t('Webportal.Subtotals')" label-class="font-weight-bold">
            <treeselect ref="propertySubtotalTreeselect"
                        class="d-inline-flex"
                        v-model="propertySubtotalValue"
                        :auto-load-root-options="false"
                        :placeholder="$t('Webportal.PropertyFilter.Placeholder')"
                        :options="propertySubtotalItems"
                        :disabled="noSurveySelected || isExporting"
                        :disableFuzzyMatching="true"
                        :multiple="false"
                        :showCount="true"
                        showCountOf="LEAF_DESCENDANTS"
                        :defaultExpandLevel="1"
                        :disable-branch-nodes="true"
                        style="max-width:20em">
            </treeselect>
            <treeselect ref="questionTreeselect"
                        class="d-inline-flex"
                        v-model="questionValue"
                        :auto-load-root-options="false"
                        :placeholder="$t('Webportal.QuestionFilter.Placeholder')"
                        :options="questionItems"
                        :disabled="noSurveySelected || isExporting"
                        :disableFuzzyMatching="true"
                        :multiple="false"
                        :showCount="true"
                        showCountOf="LEAF_DESCENDANTS"
                        :defaultExpandLevel="1"
                        :disable-branch-nodes="true"
                        style="max-width:50em">
            </treeselect>
          </b-form-group>

          <b-form-group v-if="isMSMUser"
                        :label-cols-sm="2" :label="$t('Webportal.Status')" label-class="font-weight-bold"
                        :disabled="isExporting">
            <b-form-group>
              <div class="form-row">
                <div class="col-auto">
                  <b-form-input v-model="statusMinValue"
                                type="number"
                                min="0"
                                max="400"
                                style="max-width:5em"></b-form-input>
                </div>
                <div class="col-auto">
                  <label class="col-form-label">{{ $t('Webportal.RangeTo') }}</label>
                </div>
                <div class="col-auto">
                  <b-form-input v-model="statusMaxValue"
                                type="number"
                                min="0"
                                max="400"
                                style="max-width:5em"></b-form-input>
                </div>
              </div>
            </b-form-group>
            <b-form-group>
              <b-form-checkbox v-model="includeTestchecks"
                              :disabled="noSurveySelected || isExporting">
                              {{ $t('Webportal.Include') }} {{ $t('Webportal.IncludeTestchecks') }}</b-form-checkbox>
            </b-form-group>
          </b-form-group>

          <div class="form-row">
            <div class="col-md-auto">
              <b-alert :show="noDataAlert"
                      dismissible
                      @dismissed="noDataAlert=0"
                      variant="warning">
                {{ $t('Webportal.NoDataFound') }}
              </b-alert>
              <b-button variant="primary"
                        :disabled="noSurveySelected || isExporting"
                        @click="downloadFrequencies()">
                {{ $t('Webportal.Download') }}
                <font-awesome-icon icon="download" />
              </b-button>
            </div>
            <div class="col-md-1" v-bind:class="{ inlineSpinner: isExporting }"></div>
          </div>
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
import AppConfig from '@/mixins/AppConfig';
import DownloadFileMixin from '@/mixins/DownloadFileMixin'

import SurveyFilter from '@/components/SurveyFilter-APP_TARGET';

import EventBus from '@/services/EventBus.js';
import SurveysService from '@/services/SurveysService'
import ReportsService from '@/services/ReportsService'
import AuthenticationService from '@/services/AuthenticationService'

export default {
  name: "Frequencies",

  components: { SurveyFilter },

  mixins: [ AppConfig, DownloadFileMixin ],

  data () {
    return {
      // Filter properties
      surveyFilter: [],
      currentFilter: null,                  // Current filter object for change detection      

      propertySubtotalItems: [],            // Data items for property subtotal treeselect
      propertySubtotalValue: null,          // Selected property subtotal items

      groupUploadQuestions: APP_TARGET === 'porschesar' ? "QuestionNumber" : null,
      questionItems: [],
      questionValue: null,

      statusMinValue: 220,                  // Customer Min/Max Values
      statusMaxValue: 220,
      includeTestchecks: false,

      isExporting: false,
      noDataAlert: 0
    }
  },

  computed: {
    isMSMUser: function () {
      return AuthenticationService.isMSMUser;
    },

    noSurveySelected: function () {
      return _.isEmpty(this.surveyFilter.computedSurveyIds);
    },

    getMinimumStatus: function() {
      if (!this.isMSMUser) {
        return null
      }

      return this.statusMinValue
    },

    getMaximumStatus: function() {
     if (!this.isMSMUser) {
        return null
      }

      return this.statusMaxValue
    },

    filter: function() {
      let result = {
        surveyIds: this.surveyFilter.computedSurveyIds,
        waveIds: this.surveyFilter.waveIds,
        scenarioIds: this.surveyFilter.scenarioIds,
        propertyIds: this.surveyFilter.propertyIds,
        locationIds: this.surveyFilter.clientLocationIds,
        surveyInstanceStatusIdMinimum: this.getMinimumStatus,
        surveyInstanceStatusIdMaximum: this.getMaximumStatus,
        includeTestchecks: this.includeTestchecks
      };

      if (this.surveyValue)
      {
        let id = this.surveyValue.substring(1);
        result.surveyIds.push(id);
      }

      return result;
    },

    options: function() {
      let result = {
        aggregationLevel: this.propertySubtotalValue,
        aggregationLevel2: this.questionValue,
        propertyUseLogicalAnd: this.surveyFilter.propertyUseLogicalAnd
      }

      return result;
    },

    defaultSubtotalItems: function()  {
      return [ { id: 'Wave', label: this.$t('Webportal.Wave') },
               { id: 'Scenario', label: this.$t('Webportal.Scenario') } ];
    },
  },

  mounted() {
    this.initializeTheme();
    EventBus.$on('LocalizationChanged', this.localizationChanged);
  },

  beforeDestroy() {
    EventBus.$off('LocalizationChanged', this.localizationChanged);
  },

  methods: {
    localizationChanged(localizationId) {
      this.getSurveyQuestions();
    },

    // Get survey structure data for the subtotal treeselect
    getSurveyProperties() {
      if (!this.noSurveySelected)
      {
        SurveysService.getSurveyCustomProperties(this.filter)
          .then(response => {
            let properties = response.data;
            if (properties && properties.length > 0)
              properties[0].children.forEach(p => p.id = `Property^${p.id}` );
            this.propertySubtotalItems = this.defaultSubtotalItems.concat(properties);
          })
      };
    },

    // Get survey structure data for the structure treeselect
    getSurveyQuestions() {
      if (!this.noSurveySelected) {
        SurveysService.getSurveyQuestions(this.surveyFilter.computedSurveyIds)
          .then(response => {
            if (this.groupUploadQuestions)
              this.questionItems = _(response.data)
                .flatMap(item => item.Questions ?? [])
                .groupBy(item => item[this.groupUploadQuestions] ?? item.QuestionText)
                .map((items, key) => ({
                  key: key,
                  id: `Question^${_.first(items).SurveyQuestionId}`,
                  label: _.first(items).QuestionText,
                  PositionInSurvey: _.max(items.map(item => item.PositionInSurvey)),
                  PositionInSection: _.max(items.map(item => item.PositionInSection)),
                  IsMultipleChoice: _.max(items.map(item => item.IsMultipleChoice))
                }))
                .orderBy([item => item.PositionInSurvey, item => item.PositionInSection, item => item.key])
                .value()
            else
              this.questionItems = response.data
                .map(survey => ({
                  id: survey.SurveyId,
                  label: survey.Title,
                  children: survey.Questions
                    ?.map(question => ({
                      id: `Question^${question.SurveyQuestionId}`,
                      label: question.QuestionText,
                      IsMultipleChoice: question.IsMultipleChoice
                    })) ?? []
                }));
          })
      }
    },

    // Called whenever the input of the survey filter changes
    loadData() {
      if (!_.isEqual(this.surveyFilter?.computedSurveyIds, this.currentFilter?.computedSurveyIds)) {
        this.currentFilter = this.surveyFilter;

        this.propertySubtotalValue = null;
        this.questionValue = null;
        this.getSurveyProperties();
        this.getSurveyQuestions();
      }
    },

    downloadFrequencies() {
      this.isExporting = true;
      this.noDataAlert = 0;

      ReportsService.getFrequencies(this.filter, this.options)
        .then(response => {
          this.isExporting = false;

          if (response.status === 204)
            this.noDataAlert = 5;
          else
            this.downloadFile(response);
        })
        .catch(_ => { this.isExporting = false; } );
    }
  }
};
</script>
